import { CustomAttribute } from '@/modules/common/types/custom-attribute';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { OpsiumUserShort } from '@/modules/common/types/opsium-user';
import { Manager } from '@/modules/projects/types/project';
import { Department } from '@/modules/settings/types/department';
import { Role } from '@/modules/settings/types/role';
import { Seniority } from '@/modules/settings/types/seniority';
import { MembershipType, TeamMemberChangeLogEntry, TeamMemberIntegration } from '@/modules/team';

import { TeamMemberBaseStatusDTO } from './dto/team-member-base-status.dto';
import { TeamMemberInvitationStatusDTO } from './dto/team-member-invitation-status.dto';

export interface TeamMember {
  id: string;
  name: string;
  email?: string;
  status: MemberStatus;
  type: TeamMemberType;
  isChampion: boolean;

  // Raw status fields for future use
  invitationStatus?: TeamMemberInvitationStatusDTO;
  baseStatus?: TeamMemberBaseStatusDTO;

  department?: Department;
  seniority?: Seniority;
  role?: Role;
  manager?: Manager;
  tags?: string[];
  membershipType?: MembershipType | null; // enum value e.g. MembershipType.InHouse
  avatarLink?: string;

  // Location fields
  country?: string;
  city?: string;
  location?: string; // Computed field: "City, Country"

  // Contact information
  phoneNumber?: string;

  // Role and management
  isManager: boolean;
  about?: string;
  dateOfJoin: Date | null;

  // Availability and capacity
  isOnDemandAvailability: boolean;
  capacityPerDay: number; // Replaces capacity
  billableCapacity: number;
  corporateFunctionCapacity: number; // Replaces corporateFunction

  // Rate information
  internalRateCurrency?: string;
  internalRateType?: InternalRateType;
  internalRate?: number;
  externalHourlyRate?: number;

  // Scheduled archivation dates
  nextArchiveDate?: string | null;
  nextUnarchiveDate?: string | null;

  // Integrations and changes
  integrations: TeamMemberIntegration[];
  changeLogs?: TeamMemberChangeLogEntry[];

  // Indicates if any related entities (manager, department, seniority, role) are soft-deleted
  hasAnyDeletedRelations: boolean;

  customAttributes?: CustomAttribute[];
}

export enum MemberStatus {
  Active = 'active',
  Invited = 'invited',
  Expired = 'expired',
  Basic = 'basic',
  Archived = 'archived',
  Placeholder = 'placeholder',
  NeedsReview = 'needsReview',
}

export enum TeamMemberType {
  TeamMember = 'teamMember',
  Placeholder = 'placeholder',
}

export enum InternalRateType {
  MonthlySalary = 'monthlySalary',
  HourlyCostRate = 'hourlyCostRate',
}

export type ShortTeamMember = OpsiumUserShort;

/**
 * Team member list item for table display.
 * Simplified version of TeamMember with only fields needed for list view.
 * Used in team members table and search results.
 */
export interface TeamMemberListItem {
  id: number;
  name: string;
  email: string | null;
  status: MemberStatus;
  manager?: Manager;
  department?: string;
  seniority?: string;
  role?: string;
  capacity?: number;
  isOnDemandAvailability?: boolean;
  billableCapacity?: number;
  corporateFunctionCapacity?: number;
  tags?: string[];
  membershipType?: MembershipType;
  country?: string;
  city?: string;
  isManager?: boolean;
  connectedIntegrations: IntegrationServices[];
  avatarLink?: string;
  nextArchiveDate?: string | null;
  nextUnarchiveDate?: string | null;
  isChampion: boolean;
}
